import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { LocalLoadSearchCriteria } from '../../shared/local-load-search-criteria.model';
import { LocalLoadService } from '../../shared/local-load.service';
import { LocalStop } from '../../shared/local-stop.model';
import { LocalLoad, LocalLoadStatus } from '../../shared/local-load.model';
import { LocalImport } from '../../shared/local-import.model';
import { DataFiltersService } from '../../../shared/data-filters/data-filters.service';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../shared/services/modal.service';
import { SearchObjectResult } from '../../../shared/search-object-result.model';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { Carrier } from '../../../user-dashboard/shared/carrier.model';
import { LoaderService } from '../../../shared/services/loader.service';
import { CarrierService } from '../../../user-dashboard/shared/carrier.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Option } from '../../../shared/data-filters/data-filter-select/data-filter-select.component';
import { LOCAL_STORAGE_KEYS } from '../../../core/constants';
import { CheckboxOption } from '../../../shared/data-filters/data-filter-checkbox/data-filter-checkbox.component';
import * as moment from 'moment';
import { FullDatePipe } from '../../../shared/pipes/full-date.pipe';
import { LocalLoadImportService } from '../../shared/local-load-import.service';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NavigationLinkService } from '../../../shared/services/navigation-link.service';

@Component({
  selector: 'app-find-loads-list',
  templateUrl: './find-loads-list.component.html',
  styleUrls: ['./find-loads-list.component.scss']
})
export class FindLoadsListComponent implements OnInit, OnDestroy {

  carrierSelectionAvailable = false;
  carriers: Carrier[]       = [];
  carriersOptions: Option[] = [];
  importsOptions: Option[]  = [];
  selectedCarrier: Carrier;
  selectedImportId: string  = null;
  selectedImport: Option    = null;

  statusOptions: CheckboxOption[] = [
    { labelKey: 'local-loads.search.list.filters.status.planned', value: LocalLoadStatus.PLANNED, checked: true },
    { labelKey: 'local-loads.search.list.filters.status.on-going', value: LocalLoadStatus.ON_GOING, checked: true },
    { labelKey: 'local-loads.search.list.filters.status.completed', value: LocalLoadStatus.COMPLETED, checked: true }
  ];

  searchResult: SearchObjectResult<LocalLoad>;

  @Output()
  onLoadSelection = new EventEmitter<LocalLoad>();

  @Output()
  onStopSelection = new EventEmitter<LocalStop>();

  @Output()
  hasSelectedLoad = new EventEmitter<boolean>();

  @Output()
  onCarrierSelection = new EventEmitter<Carrier>();

  selectedLoad: LocalLoad;
  selectedStop: LocalStop;

  filtersBase: LocalLoadSearchCriteria = {
    shipment_number: [],
    loadNumbers: [],
    status: [LocalLoadStatus.PLANNED, LocalLoadStatus.ON_GOING, LocalLoadStatus.COMPLETED],
    showUnassigned: false
  };

  filters: LocalLoadSearchCriteria = { ...this.filtersBase };

  lastSearchFilters: LocalLoadSearchCriteria = { ...this.filters };

  plannedStartDate: moment.Moment;
  plannedEndDate: moment.Moment;

  resetSubscription: Subscription;

  loadPage: (page: number, pageSize: number) => Observable<SearchObjectResult<LocalLoad>> = null;
  searchVehicles: () => Observable<string[]>                                              = null;
  searchImports: () => Observable<LocalImport[]>                                          = null;
  assignLoadsToVehicle: (loadUuids: string[], vehicle: string) => Observable<void>        = null;
  unassignLoadsFromVehicle: (loadUuids: string[]) => Observable<void>                     = null;

  vehicleSearchList: string[] = [];

  chosenVehicle: string = null;
  vehicleList: string[] = [];
  canAssignVehicle      = true;
  userCanManageLocations: boolean;

  constructor(
    private authService: AuthenticationService,
    private loader: LoaderService,
    private translate: TranslateService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private localLoadsService: LocalLoadService,
    private localLoadImportService: LocalLoadImportService,
    private carrierService: CarrierService,
    private fullDatePipe: FullDatePipe,
    private filtersService: DataFiltersService,
    private route: ActivatedRoute,
    private router: Router,
    public link: NavigationLinkService,
  ) {
    this.resetSubscription = this.filtersService.resetObservable.subscribe(() => this.cleanSelectedLoad());
    this.userCanManageLocations = this.authService.canManageLocations();

    if (this.authService.canManageCarriers()) {
      this.carrierSelectionAvailable = true;

      this.loadCarriers().subscribe(() => {
        if (this.selectedCarrier) {
          this.loadPage = (p, pSize) => this.localLoadsService.searchForCarrier(p - 1, pSize, this.selectedCarrier.id, this.filters);
        }

        this.searchVehicles           = () => this.localLoadsService.getVehiclesForCarrier(this.selectedCarrier.id);
        this.searchImports            = () => this.localLoadImportService.getLastImportsForCarrier(this.selectedCarrier.id);
        this.assignLoadsToVehicle     = (loadUuids, vehicle) =>
          this.localLoadsService.assignLoadsToVehicleForCarrier(this.selectedCarrier.id, loadUuids, vehicle);
        this.unassignLoadsFromVehicle = (loadUuids) =>
          this.localLoadsService.unassignLoadsFromVehicleForCarrier(this.selectedCarrier.id, loadUuids)
      });
    } else {
      this.selectedCarrier = this.authService.getUserProfile().carrier;
      this.selectCarrier(this.selectedCarrier);

      this.loadPage = (p, pSize) => this.localLoadsService.searchForSelfCarrier(p - 1, pSize, this.filters);

      this.searchVehicles           = () => this.localLoadsService.getVehiclesForSelfCarrier();
      this.assignLoadsToVehicle     = (loadUuids, vehicle) => this.localLoadsService.assignLoadsToVehicleForSelfCarrier(loadUuids, vehicle);
      this.searchImports            = () => this.localLoadImportService.getLastImportsForSelfCarrier();
      this.unassignLoadsFromVehicle = (loadUuids) => this.localLoadsService.unassignLoadsFromVehicleForSelfCarrier(loadUuids);
    }
  }

  ngOnInit() {
    let statusList          = [LocalLoadStatus.PLANNED, LocalLoadStatus.ON_GOING, LocalLoadStatus.COMPLETED];
    let vehiclesList        = [];
    let loadNumbersList     = [];
    let shipmentNumbersList = [];
    this.route.queryParams.pipe(take(1))
      .subscribe(params => {
        if (Array.isArray(params.status)) {
          statusList = params.status;
        } else if (params.status) {
          statusList.push(params.status);
        }

        if (Array.isArray(params.vehicles)) {
          vehiclesList = params.vehicles;
        } else if (params.vehicles) {
          vehiclesList.push(params.vehicles);
        }

        if (Array.isArray(params.loadNumbers)) {
          loadNumbersList = params.loadNumbers;
        } else if (params.loadNumbers) {
          loadNumbersList.push(params.loadNumbers);
        }

        if (Array.isArray(params.shipment_number)) {
          shipmentNumbersList = params.shipment_number;
        } else if (params.shipment_number) {
          shipmentNumbersList.push(params.shipment_number);
        }

        this.filters.importId            = params.importId;
        this.filters.vehicles            = vehiclesList;
        this.filters.loadNumbers         = loadNumbersList;
        this.filters.status              = statusList;
        this.filters.planned_start_date  = params.planned_start_date ? +params.planned_start_date : null;
        this.filters.planned_end_date    = params.planned_end_date ? +params.planned_end_date : null;
        this.filters.showUnassigned      = params.showUnassigned;
        this.filters.showCompleted       = params.showCompleted;
        this.filters.shipment_number     = shipmentNumbersList;

        this.applyFilters(this.filters);
      });
  }

  applyFilters(filters: LocalLoadSearchCriteria) {
    if (filters.status.length) {
      this.statusOptions.forEach(statusOption => {
        statusOption.checked = filters.status.includes(statusOption.value);
      });
    }
    this.filters           = filters;
    this.lastSearchFilters = { ...this.filters };
    if (filters.importId) {
      this.selectedImportId = filters.importId;
    }

    if (filters.planned_start_date) {
      this.plannedStartDate = moment(filters.planned_start_date);
    }

    if (filters.planned_end_date) {
      this.plannedEndDate = moment(filters.planned_end_date);
    }

  }

  loadCarriers(): Observable<Carrier[]> {
    this.loader.startLoad(false);

    return this.carrierService.getAllLight()
      .pipe(
        finalize(() => this.loader.stopLoad()),
        tap(carriers => {
          this.carriers        = carriers;
          this.carriersOptions = carriers.map(c => ({ value: c, label: c.name }));

          const carrierIdFromLocalStorageObservable = of(localStorage.getItem(LOCAL_STORAGE_KEYS.LOCAL_LOADS_CARRIER));
          const carrierIdFromQueryParamsObservable  = this.route.queryParams.pipe(
            take(1),
            map(params => params.carrier_id),
          );

          forkJoin([carrierIdFromLocalStorageObservable, carrierIdFromQueryParamsObservable])
            .subscribe(([carrierIdFromLocalStorage, carrierIdFromQueryParams]) => {
              if (carrierIdFromQueryParams) {
                this.selectedCarrier = carriers.find(c => c.id === carrierIdFromQueryParams);
              } else if (carrierIdFromLocalStorage) {
                this.selectedCarrier = carriers.find(c => c.id === carrierIdFromLocalStorage);
              } else {
                this.selectedCarrier = this.carriers.length > 0 ? this.carriers[0] : null;
              }

              if (this.selectedCarrier) {
                this.selectCarrier(this.selectedCarrier);
              }
            });
        }),
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          this.toastr.error(error.error.message || this.translate.instant('local-loads.search.errors.get-carriers'));

          return of([]);
        })
      );
  }

  selectLoad(load: LocalLoad) {
    if (this.selectedLoad && this.selectedLoad === load) {
      this.selectedLoad = null;
    } else {
      this.selectedLoad = load;
    }

    if (this.selectedLoad) {
      this.selectedLoad.expand();
    }

    this.onLoadSelection.emit(this.selectedLoad);
  }

  selectStop(stop: LocalStop) {
    this.selectedStop = stop;
    this.onStopSelection.emit(this.selectedStop);
  }

  selectCarrier(carrier: Carrier) {
    this.onCarrierSelection.emit(carrier);
    this.updateCanAssignVehicle();
  }

  updateCanAssignVehicle() {
    this.canAssignVehicle = this.selectedCarrier && this.selectedCarrier.canAssignDriverInCmaweb();
  }

  filterByImport(localImport: LocalImport) {
    const filteredImport = this.importsOptions.find(i => i.value === localImport.id);
    if (!filteredImport) {
      const importLabel   = this.translate.instant(localImport.source === 'FILE' ?
        'local-loads.search.list.importFILE' : 'local-loads.search.list.importTMS',
        { importId: this.fullDatePipe.transform(localImport.importedAt, true, true) });
      this.selectedImport = { label: importLabel, value: localImport.id };
      this.importsOptions = [...this.importsOptions, this.selectedImport]
    } else {
      this.selectedImport = filteredImport;
    }
    this.filters.importId = localImport.id;
    this.selectedImportId = localImport.id;
    event.stopPropagation();
  }

  filterByLoadNumber(loadNumber: string) {
    if (this.filters.loadNumbers === undefined) {
      this.filters.loadNumbers = [];
    }
    if (this.filters.loadNumbers.indexOf(loadNumber) === -1) {
      this.filters.loadNumbers = [...this.filters.loadNumbers, loadNumber];

    }
    event.stopPropagation();
  }

  filterByShipmentNumbers(shipmentNumbers: string) {
    shipmentNumbers = shipmentNumbers.replace('#', '')
      .replace(' ', '');
    if (this.filters.shipment_number === undefined) {
      this.filters.shipment_number = [];
    }
    if (this.filters.shipment_number.indexOf(shipmentNumbers) === -1) {
      this.filters.shipment_number = [...this.filters.shipment_number, ...shipmentNumbers.split(',')];

    }
    event.stopPropagation();
  }

  filterByVehicle(vehicle: string) {
    if (this.filters.vehicles === undefined) {
      this.filters.vehicles = [];
    }
    if (this.filters.vehicles.indexOf(vehicle) === -1) {
      this.filters.vehicles = [...this.filters.vehicles, vehicle];
    }
    event.stopPropagation();
  }

  cleanSelectedLoad() {
    this.selectLoad(null);
    this.selectStop(null);
  }

  openConfirmExportLoads(content) {
    this.modalService.open({
      title: this.translate.instant('local-loads.search.modals.export.title'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.search.list.filters.export'),
        icon: 'fa fa-download'
      },
      closeButton: {
        text: this.translate.instant('local-loads.search.modals.export.close'),
      },
      size: 'sm'
    }).subscribe(() => this.exportLoads());
  }

  exportLoads() {
    if (this.authService.canManageCarriers()) {
      this.localLoadsService.exportLocalLoadsForCarrier(this.selectedCarrier, this.lastSearchFilters);
    } else {
      this.localLoadsService.exportLocalLoadsForSelfCarrier(this.lastSearchFilters);
    }
  }

  pageChanged() {
    this.lastSearchFilters = { ...this.filters };

    if (this.carrierSelectionAvailable && this.selectedCarrier) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.LOCAL_LOADS_CARRIER, this.selectedCarrier.id);
    }

    this.cleanSelectedLoad();
  }

  onLoad(searchResult: SearchObjectResult<LocalLoad>) {
    this.searchResult = searchResult;

    const params: Params = {
      ...this.filters
    };

    if (this.carrierSelectionAvailable) {
      params.carrier_id = this.selectedCarrier.id;
    }

    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: params,
      }
    );

    this.loader.startLoad();
    this.searchVehicles()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(vehicles => {
        this.vehicleSearchList = vehicles;
      });
    this.searchImports()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(imports => {
        const importSearchList = imports.map(i => {
          const importLabel = this.translate.instant(i.source === 'FILE' ?
            'local-loads.search.list.importFILE' : 'local-loads.search.list.importTMS',
            { importId: this.fullDatePipe.transform(i.importedAt, true, true) });
          return { value: i.id, label: importLabel }
        });
        if (this.selectedImport) {
          const filteredImport = importSearchList.find(i => i.value === this.selectedImport.value);
          if (!filteredImport) {
            this.importsOptions = [{ label: 'All imports', value: null }, this.selectedImport, ...importSearchList];
          }
        } else {
          this.importsOptions = [{ label: 'All imports', value: null }, ...importSearchList];
        }
      });
  }

  beforeReset() {
    this.statusOptions.forEach((statusOption) => {
      statusOption.checked = true;
    });
    this.selectedImportId  = null;
    this.selectedImport    = null;
    this.plannedStartDate = null;
    this.plannedEndDate   = null;
  }

  updatePlannedDateFilters() {
    if (this.plannedStartDate) {
      this.filters.planned_start_date = this.plannedStartDate.utc(true).valueOf();
    } else {
      this.filters.planned_start_date = null;
    }

    if (this.plannedEndDate) {
      this.filters.planned_end_date = this.plannedEndDate.utc(true).valueOf();
    } else {
      this.filters.planned_end_date = null;
    }
  }

  ngOnDestroy() {
    this.resetSubscription.unsubscribe();
  }

  openChoseVehicleList(content, loads: LocalLoad[]) {
    this.loader.startLoad();

    this.searchVehicles()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(vehicles => {
        this.vehicleList = vehicles;
      });

    this.modalService.open({
      title: this.translate.instant('local-loads.update.assign'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.update.assign'),
        disabled: () => this.chosenVehicle === null,
        icon: 'fa fa-truck-loading'
      },
      closeButton: {
        text: this.translate.instant('local-loads.update.close')
      },
      size: 'sm'
    }).subscribe(() => {
      const selectedLoads = loads.filter(item => item.isSelected);
      this.assignLoadsToVehicle(selectedLoads.map(item => item.loadUuid), this.chosenVehicle)
        .subscribe(() => {
          selectedLoads.map(selectedLoad => {
            selectedLoad.vehicle    = this.chosenVehicle;
            selectedLoad.isSelected = false;
          });
          this.onLoad(this.searchResult);
          this.toastr.success(this.translate.instant('local-loads.update.success.assign-vehicle', { vehicle: this.chosenVehicle }));
          this.chosenVehicle = null;
        }, err => {
          console.error(err);
          this.toastr.error(this.translate.instant('local-loads.update.errors.assign-vehicle'));
        });
    });
  }

  openUnassignLoads(content, loads: LocalLoad[]) {
    this.modalService.open({
      title: this.translate.instant('local-loads.search.modals.unassign.title'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.search.modals.unassign.confirm')
      },
      closeButton: {
        text: this.translate.instant('local-loads.search.modals.unassign.close')
      },
      size: 'sm'
    }).subscribe(() => {
      const selectedLoads = loads.filter(item => item.isSelected);
      this.unassignLoadsFromVehicle(selectedLoads.map(item => item.loadUuid)).subscribe(() => {
        selectedLoads.map((selectedLoad) => {
          selectedLoad.vehicle    = null;
          selectedLoad.isSelected = false;
        });
        this.onLoad(this.searchResult);
        this.toastr.success(this.translate.instant('local-loads.update.success.unassign-vehicle'));
        this.chosenVehicle = null;
      }, err => {
        console.log(err);
        this.toastr.error(this.translate.instant('local-loads.update.errors.unassign-vehicle'));
      });
    });
  }

  selectAllLoads(loads) {
    const selectAll = this.someNotSelected(loads);
    loads.forEach(load => {
      load.isSelected = selectAll;
    });
  }

  noneSelected(items) {
    return items.filter(item => item.isSelected).length <= 0
  }

  someNotSelected(items) {
    return items.some(item => !item.isSelected);
  }

  hasSelectedLoads(): boolean {
    if (this.searchResult) {
      const loadsSelected = this.searchResult.results.filter(load => load.isSelected).length;
      this.hasSelectedLoad.emit(!!loadsSelected);
      return !!loadsSelected;
    } else {
      return false;
    }
  }

  updateImportIdFilters() {
    if (this.selectedImportId) {
      this.filters.importId = this.selectedImportId;
    } else {
      this.filters.importId = null;
    }
  }
}
